import { useState, useEffect } from "react";
import { 
  History, FileText, Calendar, Clock, ChevronRight, Copy, Check,
  Download, X, User, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  resultados: "Interpretación de resultados",
  seguimiento: "Seguimiento / control",
  medicacion: "Medicación",
  recetas: "Receta",
  documentacion: "Documentación",
  otra: "Otra consulta"
};

const STATUS_LABELS = {
  Created: "Creada",
  PendingPayment: "Pago pendiente",
  Paid: "Pagada",
  InReview: "En revisión",
  Responded: "Respondida",
  Resolved: "Resuelta",
  ESCALATED: "Escalada",
  AWAITING_INFO: "Esperando info",
  INFO_RECEIVED: "Info recibida",
  CLOSED_TIMEOUT: "Cerrada (timeout)"
};

/**
 * PatientHistoryPanel - Shows consultation history for a patient
 */
const PatientHistoryPanel = ({ 
  patientDni, 
  patientName,
  currentConsultId, 
  onUseResponse,
  isOpen,
  onClose 
}) => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [totalConsults, setTotalConsults] = useState(0);
  const [selectedConsult, setSelectedConsult] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (isOpen && patientDni) {
      loadHistory();
    }
  }, [isOpen, patientDni]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/patients/${patientDni}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Error loading history");
      
      const data = await response.json();
      // Filter out current consult
      const filteredConsults = data.consults.filter(c => c.id !== currentConsultId);
      setHistory(filteredConsults);
      setTotalConsults(filteredConsults.length);
    } catch (error) {
      console.error("Error loading patient history:", error);
      toast.error("Error al cargar historial");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyResponse = async (consult) => {
    if (!consult.response_text) return;
    
    try {
      await navigator.clipboard.writeText(consult.response_text);
      setCopiedId(consult.id);
      toast.success("Respuesta copiada al portapapeles");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error("Error al copiar");
    }
  };

  const handleUseResponse = (consult) => {
    if (onUseResponse && consult.response_text) {
      onUseResponse(consult.response_text);
      toast.success("Respuesta cargada en el editor");
      onClose();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      resultados: "bg-purple-100 text-purple-700",
      seguimiento: "bg-blue-100 text-blue-700",
      medicacion: "bg-green-100 text-green-700",
      documentacion: "bg-amber-100 text-amber-700",
      otra: "bg-slate-100 text-slate-700"
    };
    return colors[category] || colors.otra;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-600" />
            Historial de {patientName || "Paciente"}
          </DialogTitle>
          <p className="text-sm text-slate-500">DNI: {patientDni}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay consultas anteriores</p>
              <p className="text-sm text-slate-400 mt-1">Esta es la primera consulta de este paciente</p>
            </div>
          ) : (
            <div className="space-y-3 pr-2">
              <p className="text-sm text-slate-600 mb-4">
                {totalConsults} consulta{totalConsults !== 1 ? 's' : ''} anterior{totalConsults !== 1 ? 'es' : ''}
              </p>
              
              {history.map((consult) => (
                <div 
                  key={consult.id}
                  className={`border rounded-lg p-4 transition-all ${
                    selectedConsult?.id === consult.id 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className={getCategoryColor(consult.category)}>
                        {CATEGORY_LABELS[consult.category] || consult.category}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {STATUS_LABELS[consult.status] || consult.status}
                      </Badge>
                    </div>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(consult.created_at)}
                    </span>
                  </div>
                  
                  {/* Reason preview */}
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                    {consult.consult_reason || "Sin motivo registrado"}
                  </p>
                  
                  {/* Response section */}
                  {consult.status === "Responded" && consult.response_text ? (
                    <>
                      {selectedConsult?.id === consult.id ? (
                        /* Expanded view */
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-slate-500">
                              Respuesta de {consult.responding_doctor_name || "Médico"}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedConsult(null)}
                              className="h-6 px-2 text-xs"
                            >
                              <X className="w-3 h-3 mr-1" />
                              Cerrar
                            </Button>
                          </div>
                          
                          <div className="bg-slate-50 rounded-md p-3 text-sm text-slate-700 max-h-48 overflow-y-auto whitespace-pre-wrap">
                            {consult.response_text}
                          </div>
                          
                          <div className="flex gap-2 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyResponse(consult)}
                              className="flex-1"
                            >
                              {copiedId === consult.id ? (
                                <>
                                  <Check className="w-4 h-4 mr-2 text-green-600" />
                                  Copiado
                                </>
                              ) : (
                                <>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Copiar respuesta
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleUseResponse(consult)}
                              className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                              <ArrowRight className="w-4 h-4 mr-2" />
                              Usar esta respuesta
                            </Button>
                          </div>
                          
                          {consult.pdf_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full mt-2 text-slate-600"
                              onClick={() => window.open(`${API_URL}${consult.pdf_url}`, '_blank')}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Descargar PDF original
                            </Button>
                          )}
                        </div>
                      ) : (
                        /* Collapsed view */
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedConsult(consult)}
                          className="w-full justify-between text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <span className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Ver respuesta
                          </span>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-slate-400 italic">
                      {consult.status === "Responded" ? "Sin texto de respuesta" : "Consulta sin responder"}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PatientHistoryPanel;
